import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusChip from "@/components/StatusChip";
import { inr, fmtDate, LIFECYCLE } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FilePlus2, Users, Download } from "lucide-react";

export default function BusinessDashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const open = cases.filter(c => !["Resolved", "Closed"].includes(c.status)).length;
    const resolved = cases.filter(c => ["Resolved", "Closed"].includes(c.status)).length;
    const resolvedPct = cases.length ? Math.round((resolved / cases.length) * 100) : 0;
    return { total: cases.length, open, resolved, resolvedPct };
  }, [cases]);

  const filtered = cases.filter(c => {
    const matchesQ = !q || (c.title.toLowerCase().includes(q.toLowerCase()) || c.case_number.toLowerCase().includes(q.toLowerCase()));
    const matchesStatus = statusFilter === "All" || c.status === statusFilter;
    return matchesQ && matchesStatus;
  });

  return (
    <div className="space-y-8" data-testid="business-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total cases" value={stats.total} />
        <KPI label="Open" value={stats.open} />
        <KPI label="Resolved" value={stats.resolved} accent />
        <KPI label="Resolved rate" value={`${stats.resolvedPct}%`} />
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/app/file"><Button data-testid="cta-file-case" className="rounded-sm bg-primary"><FilePlus2 className="h-4 w-4 mr-2" />File a Case</Button></Link>
        <Link to="/app/bulk"><Button data-testid="cta-bulk-import" variant="outline" className="rounded-sm"><Upload className="h-4 w-4 mr-2" />Bulk Import (CSV)</Button></Link>
        <Link to="/app/team"><Button data-testid="cta-team" variant="outline" className="rounded-sm"><Users className="h-4 w-4 mr-2" />Team</Button></Link>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/export/csv`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="rounded-sm" data-testid="export-csv"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        </a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/export/pdf`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="rounded-sm" data-testid="export-pdf"><Download className="h-4 w-4 mr-2" />Export PDF</Button>
        </a>
      </div>

      <div className="bg-card rounded-sm border border-stone/60 p-6 ">
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <Input
            data-testid="cases-search"
            placeholder="Search by title or case #"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-sm h-10 rounded-sm"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="cases-status-filter"
            className="h-10 rounded-sm border border-stone/60 bg-card px-3 text-sm"
          >
            <option>All</option>
            {LIFECYCLE.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <CasesTable loading={loading} cases={filtered} />
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-sm p-6 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-card border-stone/60"} `} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-4xl font-semibold mt-2 text-court">{value}</div>
    </div>
  );
}

function CasesTable({ loading, cases }) {
  if (loading) return <div className="text-muted-foreground p-4">Loading…</div>;
  if (cases.length === 0) return <div className="text-muted-foreground p-4">No cases match your filters.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
            <th className="p-3">Case #</th><th className="p-3">Title</th><th className="p-3">Type</th><th className="p-3">Claim</th><th className="p-3">Status</th><th className="p-3">Filed</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {cases.map(c => (
            <tr key={c.id} className="hover:bg-slate-50 transition" data-testid={`case-row-${c.case_number}`}>
              <td className="p-3 font-mono text-xs">{c.case_number}</td>
              <td className="p-3 font-medium"><Link to={`/app/cases/${c.id}`} className="hover:underline text-court">{c.title}</Link></td>
              <td className="p-3 capitalize">{c.type}</td>
              <td className="p-3 font-semibold">{inr(c.claim_amount)}</td>
              <td className="p-3"><StatusChip status={c.status} /></td>
              <td className="p-3 text-muted-foreground">{fmtDate(c.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
